/**
 * @file pages/SingleTour.jsx
 * @description Single tour detail page with interactive floating booking widget.
 * Features date selection, guest count with live price, availability check, and booking form.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { MapPin, Calendar, Users, Clock, Star, Check, X, AlertCircle, Loader2, Phone, Mail, User, MessageSquare, ChevronDown, ChevronUp, Shield, Tag, Handshake } from 'lucide-react';
import { toast } from 'react-toastify';
import Header from "../Components/Layouts/Header";
import Footer from "../Components/Layouts/Footer";
import ItineraryTimeline from "../Components/Tours/ItineraryTimeline";
import NegotiationModal from "../Components/Tours/NegotiationModal";
import { getTour } from "../services/api";
import bookingService from "../services/bookingService";

const SingleTour = () => {
  const { id } = useParams();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImage, setActiveImage] = useState(0);

  // Booking widget state
  const [selectedDate, setSelectedDate] = useState('');
  const [guestCount, setGuestCount] = useState(1);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    customerName: '',
    userEmail: '',
    phone: '',
    specialRequests: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [showMobileWidget, setShowMobileWidget] = useState(false);
  const [showNegotiationModal, setShowNegotiationModal] = useState(false);

  useEffect(() => {
    const fetchTour = async () => {
      try {
        const response = await getTour(id);
        setTour(response.data.data.tour);
        setError(null);
      } catch (err) {
        setError('Failed to load tour details. Please try again later.');
        console.error('Error fetching tour:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTour();
  }, [id]);

  // Check availability when date or guest count changes
  const checkAvailability = useCallback(async () => {
    if (!selectedDate || !tour) return;
    setCheckingAvailability(true);
    try {
      const result = await bookingService.checkAvailability(tour._id, selectedDate, guestCount);
      setAvailability(result.data);
    } catch (err) {
      console.error('Availability check failed:', err);
      setAvailability(null);
    } finally {
      setCheckingAvailability(false);
    }
  }, [selectedDate, guestCount, tour]);

  useEffect(() => {
    if (selectedDate && tour) {
      const timer = setTimeout(checkAvailability, 300);
      return () => clearTimeout(timer);
    }
  }, [selectedDate, guestCount, checkAvailability, tour]);

  // Live price calculation with group discount
  const minGroupSize = tour?.pricing?.minGroupSize || 10;
  const isLargeGroup = guestCount >= minGroupSize;
  const perPersonPrice = tour
    ? (isLargeGroup && tour.pricing?.group ? tour.pricing.group : tour.price)
    : 0;
  const totalPrice = perPersonPrice * guestCount;

  // Form validation
  const validateForm = () => {
    const errors = {};
    if (!bookingForm.customerName.trim()) errors.customerName = 'Name is required';
    if (!bookingForm.userEmail.trim()) errors.userEmail = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingForm.userEmail)) errors.userEmail = 'Invalid email';
    if (!selectedDate) errors.date = 'Please select a date';
    else {
      const d = new Date(selectedDate);
      const today = new Date(); today.setHours(0,0,0,0);
      if (d < today) errors.date = 'Date must be in the future';
    }
    if (guestCount < 1) errors.guestCount = 'At least 1 guest';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBookingSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!validateForm()) return;
    if (availability && !availability.available) {
      toast.error('This date is sold out. Please choose another date.');
      return;
    }

    setBookingSubmitting(true);
    try {
      await bookingService.createBooking({
        tour: tour._id,
        customerName: bookingForm.customerName.trim(),
        userEmail: bookingForm.userEmail.trim(),
        phone: bookingForm.phone.trim(),
        bookingDate: selectedDate,
        guestCount,
        totalPrice,
        specialRequests: bookingForm.specialRequests.trim(),
      });
      setBookingSuccess(true);
      toast.success('Booking confirmed! Check your email for details.');
    } catch (err) {
      const msg = err?.message || 'Booking failed. Please try again.';
      toast.error(msg);
    } finally {
      setBookingSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#243185] border-t-transparent"></div>
          <p className="text-[#243185] text-lg">Loading tour details...</p>
        </div>
      </div>
    );
  }

  if (error || !tour) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-red-600 text-lg">{error || 'Tour not found'}</p>
          <Link
            to="/tours"
            className="inline-block px-6 py-2 bg-[#243185] text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Tours
          </Link>
        </div>
      </div>
    );
  }

  // Build available dates (only future dates)
  const today = new Date(); today.setHours(0,0,0,0);
  const futureDates = (tour.startDates || [])
    .map(d => new Date(d))
    .filter(d => d >= today)
    .sort((a, b) => a - b);

  // Combine thumbnail + gallery images into one array for display
  const allImages = [
    ...(tour.thumbnail ? [tour.thumbnail] : []),
    ...(tour.images || []).filter(img => img !== tour.thumbnail),
  ];
  if (allImages.length === 0) allImages.push('https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=800&q=80');

  // JSON-LD structured data for SEO
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: tour.title,
    description: tour.description,
    image: allImages,
    offers: {
      '@type': 'Offer',
      price: tour.price,
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
    aggregateRating: tour.rating ? {
      '@type': 'AggregateRating',
      ratingValue: tour.rating,
      reviewCount: tour.reviewCount || 1,
      bestRating: 5,
    } : undefined,
    brand: { '@type': 'Organization', name: 'Nikaba Travels' },
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{tour.title} | Nikaba Travels</title>
        <meta name="description" content={tour.description?.substring(0, 160)} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Header />
      
      {/* Hero Section */}
      <div className="relative h-[60vh] overflow-hidden">
        <img
          src={allImages[activeImage] || allImages[0]}
          alt={tour.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40 flex items-center">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <span className="inline-block bg-white/90 backdrop-blur-sm text-[#243185] text-sm font-semibold px-3 py-1 rounded-full mb-4">
                {tour.category.charAt(0).toUpperCase() + tour.category.slice(1)}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{tour.title}</h1>
              <div className="flex items-center gap-4 text-white">
                <div className="flex items-center">
                  <MapPin size={20} className="mr-2" />
                  <span>{tour.location}</span>
                </div>
                <div className="flex items-center">
                  <Star size={20} className="text-yellow-400 fill-current mr-1" />
                  <span>{tour.rating.toFixed(1)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Thumbnail Gallery */}
      {allImages.length > 1 && (
        <div className="container mx-auto px-4 -mt-8 mb-8 relative z-10">
          <div className="flex gap-2 overflow-x-auto pb-4">
            {allImages.map((image, index) => (
              <button
                key={index}
                onClick={() => setActiveImage(index)}
                className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                  activeImage === index ? 'border-[#243185]' : 'border-transparent'
                }`}
              >
                <img src={image} alt={`${tour.title} ${index + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Tour Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Tour Overview</h2>
              <p className="text-gray-600 leading-relaxed">{tour.description}</p>
            </div>

            {/* Highlights */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Highlights</h2>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {tour.highlights && tour.highlights.length > 0 && tour.highlights.map((highlight, index) => (
                  <li key={index} className="flex items-start">
                    <Check size={20} className="text-green-500 mr-2 flex-shrink-0 mt-1" />
                    <span className="text-gray-600">{highlight}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* What's Included/Not Included */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">What's Included</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Included</h3>
                  <ul className="space-y-2">
                    {tour.included && tour.included.length > 0 && tour.included.map((item, index) => (
                      <li key={index} className="flex items-center text-gray-600">
                        <Check size={18} className="text-green-500 mr-2" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Not Included</h3>
                  <ul className="space-y-2">
                    {tour.notIncluded && tour.notIncluded.length > 0 && tour.notIncluded.map((item, index) => (
                      <li key={index} className="flex items-center text-gray-600">
                        <X size={18} className="text-red-500 mr-2" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Itinerary Timeline */}
            {tour.itinerary && tour.itinerary.length > 0 && (
              <ItineraryTimeline itinerary={tour.itinerary} />
            )}

            {/* Category-specific details */}
            {tour.category === 'adventurous' && tour.details?.gearChecklist?.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="bg-orange-100 text-orange-600 px-2 py-1 rounded-lg text-sm font-bold">
                    {tour.difficulty?.toUpperCase() || 'MODERATE'}
                  </span>
                  Gear Checklist
                </h2>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {tour.details.gearChecklist.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-600 text-sm">
                      <Check size={16} className="text-orange-500 flex-shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {tour.category === 'educational' && (
              <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Educational Details</h2>
                {tour.details?.studentTeacherRatio && (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Users size={16} className="text-green-500" />
                    <span className="font-medium">Student-Teacher Ratio:</span> {tour.details.studentTeacherRatio}
                  </div>
                )}
                {tour.details?.learningOutcomes?.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Learning Outcomes</h3>
                    <ul className="space-y-1">
                      {tour.details.learningOutcomes.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-600 text-sm">
                          <Check size={14} className="text-green-500 mt-0.5 flex-shrink-0" /> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {tour.details?.safetyProtocols?.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Safety Protocols</h3>
                    <ul className="space-y-1">
                      {tour.details.safetyProtocols.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-600 text-sm">
                          <Shield size={14} className="text-blue-500 mt-0.5 flex-shrink-0" /> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ═══════════ Floating Booking Widget (Desktop) ═══════════ */}
          <div className="lg:col-span-1 hidden lg:block">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 sticky top-8 overflow-hidden">
              {bookingSuccess ? (
                /* Success State */
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check size={32} className="text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Booking Confirmed!</h3>
                  <p className="text-gray-500 mb-4">We've sent a confirmation to your email. Our team will review and confirm shortly.</p>
                  <button
                    onClick={() => { setBookingSuccess(false); setShowBookingForm(false); setSelectedDate(''); setGuestCount(1); }}
                    className="text-[#243185] font-medium hover:underline"
                  >
                    Make another booking
                  </button>
                </div>
              ) : (
                <>
                  {/* Price Header */}
                  <div className="bg-gradient-to-r from-[#243185] to-[#1a2460] p-5">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-white">₹{tour.price}</span>
                      <span className="text-white/70 text-sm">/person</span>
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-white/80 text-sm">
                      <span className="flex items-center gap-1"><Clock size={14} /> {tour.duration} days</span>
                      <span className="flex items-center gap-1"><Users size={14} /> Max {tour.groupSize}</span>
                    </div>
                  </div>

                  <div className="p-5 space-y-4">
                    {/* Date Selection */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Select Date</label>
                      {futureDates.length > 0 ? (
                        <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                          {futureDates.map((date, index) => {
                            const dateStr = date.toISOString().split('T')[0];
                            const isSelected = selectedDate === dateStr;
                            return (
                              <button
                                key={index}
                                onClick={() => setSelectedDate(dateStr)}
                                className={`w-full flex items-center justify-between p-2.5 rounded-lg border text-sm transition-all ${
                                  isSelected
                                    ? 'border-[#243185] bg-[#243185]/5 text-[#243185] ring-1 ring-[#243185]'
                                    : 'border-gray-200 hover:border-[#243185]/50 text-gray-700'
                                }`}
                              >
                                <span className="flex items-center gap-2">
                                  <Calendar size={15} className={isSelected ? 'text-[#243185]' : 'text-gray-400'} />
                                  {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </span>
                                {isSelected && <Check size={16} className="text-[#243185]" />}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <div>
                          <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#243185]/20 focus:border-[#243185] outline-none"
                          />
                        </div>
                      )}
                      {formErrors.date && <p className="text-red-500 text-xs mt-1">{formErrors.date}</p>}
                    </div>

                    {/* Guest Count */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Number of Guests</label>
                      <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                        <button
                          onClick={() => setGuestCount(Math.max(1, guestCount - 1))}
                          className="px-4 py-2.5 text-gray-600 hover:bg-gray-100 transition-colors font-bold text-lg"
                        >−</button>
                        <div className="flex-1 text-center font-semibold text-gray-900 border-x border-gray-300 py-2.5">
                          {guestCount} {guestCount === 1 ? 'Guest' : 'Guests'}
                        </div>
                        <button
                          onClick={() => setGuestCount(Math.min(tour.groupSize, guestCount + 1))}
                          className="px-4 py-2.5 text-gray-600 hover:bg-gray-100 transition-colors font-bold text-lg"
                        >+</button>
                      </div>
                      {formErrors.guestCount && <p className="text-red-500 text-xs mt-1">{formErrors.guestCount}</p>}
                    </div>

                    {/* Availability Status */}
                    {selectedDate && (
                      <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                        checkingAvailability ? 'bg-gray-50 text-gray-500' :
                        availability?.available ? 'bg-green-50 text-green-700' :
                        'bg-red-50 text-red-700'
                      }`}>
                        {checkingAvailability ? (
                          <><Loader2 size={16} className="animate-spin" /> Checking availability...</>
                        ) : availability?.available ? (
                          <><Check size={16} /> {availability.remainingSpots} spot{availability.remainingSpots !== 1 ? 's' : ''} remaining</>
                        ) : availability ? (
                          <><AlertCircle size={16} /> Sold out for this date</>
                        ) : null}
                      </div>
                    )}

                    {/* Large Group Discount Badge */}
                    {isLargeGroup && (
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                        <Tag size={16} className="flex-shrink-0" />
                        <div>
                          <span className="font-semibold">Large Group Discount Applied!</span>
                          <span className="block text-xs text-amber-600">
                            ₹{perPersonPrice}/person (was ₹{tour.price})
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Live Price */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between text-sm text-gray-600 mb-1">
                        <span>₹{perPersonPrice} × {guestCount} guest{guestCount > 1 ? 's' : ''}</span>
                        <span>₹{totalPrice.toLocaleString()}</span>
                      </div>
                      {isLargeGroup && (
                        <div className="flex justify-between text-xs text-green-600 mb-1">
                          <span>Group savings</span>
                          <span>-₹{((tour.price - perPersonPrice) * guestCount).toLocaleString()}</span>
                        </div>
                      )}
                      <div className="border-t border-gray-200 mt-2 pt-2 flex justify-between font-bold text-lg">
                        <span>Total</span>
                        <span className="text-[#243185]">₹{totalPrice.toLocaleString()}</span>
                      </div>
                    </div>

                    {/* Negotiate Price Button (for large groups) */}
                    {isLargeGroup && (
                      <button
                        onClick={() => setShowNegotiationModal(true)}
                        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-[#243185]/30 text-[#243185] text-sm font-medium hover:bg-[#243185]/5 transition-all"
                      >
                        <Handshake size={16} /> Negotiate Price
                      </button>
                    )}

                    {/* Booking Form Toggle / Submit */}
                    {!showBookingForm ? (
                      <button
                        onClick={() => {
                          if (!selectedDate) { setFormErrors({ date: 'Please select a date first' }); return; }
                          if (availability && !availability.available) { toast.error('Sold out for this date'); return; }
                          setShowBookingForm(true);
                        }}
                        className="w-full bg-[#243185] text-white py-3.5 rounded-xl hover:bg-[#1a2460] transition-all font-semibold text-base shadow-md hover:shadow-lg"
                      >
                        Book Now
                      </button>
                    ) : (
                      <form onSubmit={handleBookingSubmit} className="space-y-3">
                        <div>
                          <div className="relative">
                            <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Full Name *"
                              value={bookingForm.customerName}
                              onChange={(e) => setBookingForm(p => ({ ...p, customerName: e.target.value }))}
                              className={`w-full pl-9 pr-3 py-2.5 border rounded-lg text-sm outline-none transition-all ${formErrors.customerName ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:ring-[#243185]/20 focus:border-[#243185]'} focus:ring-2`}
                            />
                          </div>
                          {formErrors.customerName && <p className="text-red-500 text-xs mt-0.5">{formErrors.customerName}</p>}
                        </div>
                        <div>
                          <div className="relative">
                            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                              type="email"
                              placeholder="Email Address *"
                              value={bookingForm.userEmail}
                              onChange={(e) => setBookingForm(p => ({ ...p, userEmail: e.target.value }))}
                              className={`w-full pl-9 pr-3 py-2.5 border rounded-lg text-sm outline-none transition-all ${formErrors.userEmail ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:ring-[#243185]/20 focus:border-[#243185]'} focus:ring-2`}
                            />
                          </div>
                          {formErrors.userEmail && <p className="text-red-500 text-xs mt-0.5">{formErrors.userEmail}</p>}
                        </div>
                        <div className="relative">
                          <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="tel"
                            placeholder="Phone (optional)"
                            value={bookingForm.phone}
                            onChange={(e) => setBookingForm(p => ({ ...p, phone: e.target.value }))}
                            className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#243185]/20 focus:border-[#243185] outline-none transition-all"
                          />
                        </div>
                        <div className="relative">
                          <MessageSquare size={16} className="absolute left-3 top-3 text-gray-400" />
                          <textarea
                            placeholder="Special requests (optional)"
                            value={bookingForm.specialRequests}
                            onChange={(e) => setBookingForm(p => ({ ...p, specialRequests: e.target.value }))}
                            rows={2}
                            className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#243185]/20 focus:border-[#243185] outline-none resize-none transition-all"
                          />
                        </div>
                        <button
                          type="submit"
                          disabled={bookingSubmitting}
                          className="w-full bg-[#243185] text-white py-3.5 rounded-xl hover:bg-[#1a2460] transition-all font-semibold text-base shadow-md hover:shadow-lg disabled:opacity-60 flex items-center justify-center gap-2"
                        >
                          {bookingSubmitting ? (
                            <><Loader2 size={18} className="animate-spin" /> Processing...</>
                          ) : (
                            <>Confirm Booking — ₹{totalPrice.toLocaleString()}</>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowBookingForm(false)}
                          className="w-full text-gray-500 text-sm hover:text-gray-700 py-1"
                        >
                          Cancel
                        </button>
                      </form>
                    )}

                    <div className="flex items-center justify-center gap-2 text-xs text-gray-400 pt-1">
                      <Shield size={12} /> Secure booking · Instant confirmation
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════ Mobile Floating Book Now Bar ═══════════ */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.1)]">
        {showMobileWidget ? (
          <div className="p-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-900">Book This Tour</h3>
              <button onClick={() => setShowMobileWidget(false)}>
                <ChevronDown size={20} className="text-gray-500" />
              </button>
            </div>

            {bookingSuccess ? (
              <div className="text-center py-6">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Check size={24} className="text-green-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-1">Booking Confirmed!</h3>
                <p className="text-gray-500 text-sm">Confirmation sent to your email.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Date */}
                {futureDates.length > 0 ? (
                  <select
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">Select a date</option>
                    {futureDates.map((date, i) => (
                      <option key={i} value={date.toISOString().split('T')[0]}>
                        {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm"
                  />
                )}

                {/* Guest Count */}
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <button onClick={() => setGuestCount(Math.max(1, guestCount - 1))} className="px-4 py-2 font-bold text-lg">−</button>
                  <div className="flex-1 text-center font-semibold border-x border-gray-300 py-2">{guestCount} Guest{guestCount > 1 ? 's' : ''}</div>
                  <button onClick={() => setGuestCount(Math.min(tour.groupSize, guestCount + 1))} className="px-4 py-2 font-bold text-lg">+</button>
                </div>

                {/* Availability */}
                {selectedDate && availability && (
                  <div className={`text-sm p-2 rounded-lg ${availability.available ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {availability.available ? `${availability.remainingSpots} spots left` : 'Sold out'}
                  </div>
                )}

                {/* Contact fields */}
                <input
                  type="text"
                  placeholder="Full Name *"
                  value={bookingForm.customerName}
                  onChange={(e) => setBookingForm(p => ({ ...p, customerName: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm"
                />
                <input
                  type="email"
                  placeholder="Email *"
                  value={bookingForm.userEmail}
                  onChange={(e) => setBookingForm(p => ({ ...p, userEmail: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm"
                />

                <button
                  onClick={handleBookingSubmit}
                  disabled={bookingSubmitting}
                  className="w-full bg-[#243185] text-white py-3 rounded-xl font-semibold disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {bookingSubmitting ? <><Loader2 size={16} className="animate-spin" /> Processing...</> : <>Confirm — ₹{totalPrice.toLocaleString()}</>}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-between p-4">
            <div>
              <span className="text-2xl font-bold text-[#243185]">₹{tour.price}</span>
              <span className="text-gray-500 text-sm"> /person</span>
            </div>
            <button
              onClick={() => setShowMobileWidget(true)}
              className="bg-[#243185] text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 shadow-md"
            >
              Book Now <ChevronUp size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Extra bottom padding on mobile for the floating bar */}
      <div className="lg:hidden h-24" />

      {/* Negotiation Modal */}
      <NegotiationModal
        isOpen={showNegotiationModal}
        onClose={() => setShowNegotiationModal(false)}
        tour={tour}
        guestCount={guestCount}
      />

      <Footer />
    </div>
  );
};

export default SingleTour;
